import { api, ApiError, errorFromResponse } from "@/lib/api";
import type { User, PlatformAdmin } from "@/types/api";

// The backend's password login returns a 303 redirect on plain success but a
// 200 JSON {status:"2fa_required"} when the role needs a TOTP step. We POST with
// redirect:"manual" so the 303 surfaces as an opaque redirect (not a followed
// HTML fetch) and we can branch cleanly. (This split is a known backend quirk;
// when the API moves to a JSON-first login this function is the only edit.)
export type LoginResult =
  | { status: "ok" }
  | { status: "2fa"; action: "verify" | "enroll" }
  // First-login: the user must replace a temporary password before continuing
  // (even before 2FA). The pending cookie from login authenticates the change.
  | { status: "password_change_required" };

async function authForm(
  path: string,
  fields: Record<string, string>,
): Promise<LoginResult> {
  const csrf = await api.ensureCsrf();
  const res = await fetch(`/api${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      "X-CSRF-Token": csrf,
    },
    body: new URLSearchParams(fields).toString(),
    credentials: "same-origin",
    redirect: "manual",
  });

  // 303 → session established.
  if (res.type === "opaqueredirect" || (res.ok && res.redirected))
    return { status: "ok" };

  if (res.status === 200) {
    const data = (await res.json().catch(() => ({}))) as {
      status?: string;
      action?: string;
    };
    if (data.status === "2fa_required") {
      return {
        status: "2fa",
        action: data.action === "enroll" ? "enroll" : "verify",
      };
    }
    if (data.status === "password_change_required") {
      return { status: "password_change_required" };
    }
    return { status: "ok" };
  }

  throw await errorFromResponse(res);
}

export const login = (email: string, password: string) =>
  authForm("/auth/login", { email, password });

// First-login forced password change: authenticated by the pending cookie set by
// login (the must-change gate), posted before 2FA. The backend continues the
// login, so this resolves to {status:"ok"} or {status:"2fa"} just like login.
export const changeFirstPassword = (password: string) =>
  authForm("/auth/password/first-change", { password });

export const verify2fa = (code: string, remember = false) =>
  authForm("/auth/2fa/verify", remember ? { code, remember: "true" } : { code });

// Enrollment data shown once when a 2FA-required user has no authenticator set up
// yet: scan the otpauthUrl (QR), or type the secret, and keep the recovery codes.
export interface EnrollData {
  otpauthUrl: string;
  secret: string;
  recoveryCodes: string[];
}

// enroll2fa provisions a TOTP secret for the user in the 2FA-pending state (the
// pending cookie was set by login). Returns the QR/secret + recovery codes.
export const enroll2fa = () => api.post<EnrollData>("/auth/2fa/enroll");

export async function me(signal?: AbortSignal): Promise<User | null> {
  try {
    return await api.get<User>("/auth/me", signal);
  } catch (err) {
    if (err instanceof ApiError && err.isUnauthorized) return null;
    throw err;
  }
}

export const logout = () => api.post<{ status: string }>("/auth/logout");

// ── Demo login (docs/04 companion) ───────────────────────────────────────────
// Signs a visitor back into an EXISTING demo tenant with the email/password
// shown once at POST /api/demo/start. Shared by DemoLoginPage (the dedicated
// page) and LoginPage, which routes into this automatically when a pasted
// email matches the demo shape (isDemoEmail) instead of requiring the visitor
// to know to use /demo/login specifically.
export const demoLogin = (email: string, password: string) =>
  api.post("/demo/login", { email, password });

// Matches the demo-tenant email shape the backend generates
// (randomDemoOwner, internal/tenancy/demo.go): "demo-" + 12 lowercase hex
// characters (6 random bytes) + "@klutch.lk". Purely a client-side UX routing
// heuristic, not a trust boundary - the backend alone decides whether a
// credential is valid, exactly like today.
const DEMO_EMAIL_PATTERN = /^demo-[0-9a-f]{12}@klutch\.lk$/;
export const isDemoEmail = (email: string) =>
  DEMO_EMAIL_PATTERN.test(email.trim().toLowerCase());

// ── Password reset (6, "Forget Password") ────────────────────────────────────
// Both are form-encoded + CSRF-protected (like login). `forgot` ALWAYS resolves
// for any well-formed email: the backend reveals nothing about which accounts
// exist. `reset` resolves on success and throws ApiError (e.g. 400 for an
// invalid/expired token or a too-short password) otherwise. (Reuses platformForm:
// a plain form POST that resolves on 2xx and throws the decoded problem+json.)
export const forgotPassword = (email: string) =>
  platformForm("/auth/password/forgot", { email });
export const resetPassword = (token: string, password: string) =>
  platformForm("/auth/password/reset", { token, password });
export const platformForgotPassword = (email: string) =>
  platformForm("/platform/password/forgot", { email });
export const platformResetPassword = (token: string, password: string) =>
  platformForm("/platform/password/reset", { token, password });

// ── Platform / super-admin (separate axis, 6.6) ──────────────────────────────
// Two-step login (mirrors the tenant flow): step 1 verifies email + password and
// arms the TOTP challenge; step 2 verifies the code and mints the session. Both
// are form-encoded, CSRF-protected, and set/use the separate platform cookies.
async function platformForm(
  path: string,
  fields: Record<string, string>,
): Promise<void> {
  const csrf = await api.ensureCsrf();
  const res = await fetch(`/api${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      "X-CSRF-Token": csrf,
    },
    body: new URLSearchParams(fields).toString(),
    credentials: "same-origin",
  });
  if (!res.ok) throw await errorFromResponse(res);
}

// Step 1: password. Platform login ALWAYS requires 2FA (no role exemption), and
// returns whether the admin must enroll (first login, no authenticator yet) or
// verify. Reuses authForm, which parses the {status,action} JSON.
export const platformLogin = (email: string, password: string) =>
  authForm("/platform/login", { email, password });

// Platform 2FA enrollment (first login): provisions a pending secret using the
// platform-pending cookie and returns the QR/secret + recovery codes (shown once).
export const platformEnroll2fa = () =>
  api.post<EnrollData>("/platform/2fa/enroll");

// Step 2: TOTP (or recovery) code, using the platform-pending cookie from step 1.
// A first successful code also confirms a pending enrollment server-side. When
// `remember` is set, the backend trusts this device so the next platform login
// skips 2FA (revocable + expiring, like the tenant flow).
export const platformVerify2fa = (code: string, remember = false) =>
  platformForm("/platform/2fa/verify", remember ? { code, remember: "true" } : { code });

export const platformLogout = () =>
  api.post<{ status: string }>("/platform/logout");

// PlatformAccount is the signed-in admin's self-service view: identity plus
// whether a forced first-login password change is still pending (6.6). It is a
// superset of PlatformAdmin, so it also drives the /platform route guard.
export interface PlatformAccount extends PlatformAdmin {
  password_change_required: boolean;
}

// platformAccount loads the account status behind the platform session. Returns
// null on 401 (not signed in), so the guard can send to /login.
export async function platformAccount(
  signal?: AbortSignal,
): Promise<PlatformAccount | null> {
  try {
    return await api.get<PlatformAccount>("/platform/account", signal);
  } catch (err) {
    if (err instanceof ApiError && err.isUnauthorized) return null;
    throw err;
  }
}

// platformChangePassword replaces the signed-in admin's password (self-service,
// and how the boot-seeded admin clears the forced-change gate). 204 on success.
export const platformChangePassword = (
  currentPassword: string,
  newPassword: string,
) =>
  api.post<void>("/platform/account/password", {
    current_password: currentPassword,
    new_password: newPassword,
  });
